import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Line } from 'react-chartjs-2';
import { 
  faPercentage, 
  faCalculator, 
  faChartBar, 
  faInfoCircle, 
  faStar, 
  faLightbulb,
  faChartLine 
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const TripleDiscountCalculator = () => {
  const [originalPrice, setOriginalPrice] = useState('');
  const [discount1, setDiscount1] = useState('');
  const [discount2, setDiscount2] = useState('');
  const [discount3, setDiscount3] = useState('');
  const [finalPrice, setFinalPrice] = useState('');
  const [totalSavings, setTotalSavings] = useState('');
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const calculateTripleDiscount = () => {
    try {
      const price = parseFloat(originalPrice);
      const d1 = parseFloat(discount1);
      const d2 = parseFloat(discount2);
      const d3 = parseFloat(discount3);

      if (isNaN(price) || price <= 0) {
        throw new Error('Please enter a valid original price');
      }

      if (isNaN(d1) || d1 < 0 || d1 > 100) {
        throw new Error('Please enter a valid first discount (0-100%)');
      }

      if (isNaN(d2) || d2 < 0 || d2 > 100) {
        throw new Error('Please enter a valid second discount (0-100%)');
      }

      if (isNaN(d3) || d3 < 0 || d3 > 100) {
        throw new Error('Please enter a valid third discount (0-100%)');
      }

      // Calculate sequential discounts
      let priceAfterFirst = price * (1 - d1 / 100);
      let priceAfterSecond = priceAfterFirst * (1 - d2 / 100);
      let final = priceAfterSecond * (1 - d3 / 100);

      const totalSaved = price - final;

      setFinalPrice(final.toFixed(2));
      setTotalSavings(totalSaved.toFixed(2));
      setError('');

      // Generate chart data
      const discounts = [d1, d2, d3];
      const prices = [price];
      
      // Calculate intermediate prices
      let currentPrice = price;
      discounts.forEach(d => {
        currentPrice *= (1 - d / 100);
        prices.push(currentPrice);
      });

      // Generate chart data
      setChartData({
        labels: ['Original', 'After 1st', 'After 2nd', 'After 3rd'],
        datasets: [
          {
            label: 'Price Sequence',
            data: prices,
            borderColor: 'rgb(75, 192, 192)',
            tension: 0.1,
            fill: false,
            pointBackgroundColor: 'rgb(75, 192, 192)',
            pointBorderColor: '#fff',
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderColor: 'rgb(75, 192, 192)',
          }
        ]
      });
    } catch (err) {
      setError(err.message);
      setFinalPrice('');
      setTotalSavings('');
      setChartData(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100">
      <Helmet>
        <title>Triple Discount Calculator</title>
        <meta name="description" content="Calculate triple discounts and savings with our advanced discount calculator. Get instant results and visualize your savings." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <FontAwesomeIcon 
            icon={faCalculator} 
            className="text-4xl text-purple-600 mb-4" 
          />
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Triple Discount Calculator</h1>
          <p className="text-xl text-gray-600">Calculate savings with three sequential discounts</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Original Price</label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                    ₹
                  </span>
                  <input
                    type="number"
                    value={originalPrice}
                    onChange={(e) => setOriginalPrice(e.target.value)}
                    className="flex-1 rounded-r-md border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                    placeholder="Enter original price"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">First Discount</label>
                <div className="flex">
                  <input
                    type="number"
                    value={discount1}
                    onChange={(e) => setDiscount1(e.target.value)}
                    className="flex-1 rounded-l-md border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                    placeholder="Enter first discount"
                  />
                  <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500">
                    %
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Second Discount</label>
                <div className="flex">
                  <input
                    type="number"
                    value={discount2}
                    onChange={(e) => setDiscount2(e.target.value)}
                    className="flex-1 rounded-l-md border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                    placeholder="Enter second discount"
                  />
                  <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500">
                    %
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Third Discount</label>
                <div className="flex">
                  <input
                    type="number"
                    value={discount3}
                    onChange={(e) => setDiscount3(e.target.value)}
                    className="flex-1 rounded-l-md border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                    placeholder="Enter third discount"
                  />
                  <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500">
                    %
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={calculateTripleDiscount}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-red-600 px-6 py-3 rounded-xl hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
              >
                Calculate Triple Discount
              </button>
            </div>

            {error && (
              <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-xl">
                {error}
              </div>
            )}

            {finalPrice && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-8">Results</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-2xl shadow-sm">
                    <div className="flex items-center mb-4">
                      <FontAwesomeIcon icon={faCalculator} className="text-purple-600 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-700">Final Price</h3>
                    </div>
                    <p className="text-3xl font-bold text-purple-600">₹{finalPrice}</p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-2xl shadow-sm">
                    <div className="flex items-center mb-4">
                      <FontAwesomeIcon icon={faPercentage} className="text-purple-600 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-700">Total Savings</h3>
                    </div>
                    <p className="text-3xl font-bold text-purple-600">₹{totalSavings}</p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-2xl shadow-sm">
                    <div className="flex items-center mb-4">
                      <FontAwesomeIcon icon={faStar} className="text-purple-600 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-700">Discount Sequence</h3>
                    </div>
                    <div className="space-y-2">
                      <p className="text-gray-600">1st Discount: {discount1}%</p>
                      <p className="text-gray-600">2nd Discount: {discount2}%</p>
                      <p className="text-gray-600">3rd Discount: {discount3}%</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <h2 className="text-xl font-semibold mb-4 text-gray-800">Price Sequence Chart</h2>
                  <div className="bg-white p-6 rounded-2xl shadow-sm">
                    <div className="flex items-center mb-4">
                      <FontAwesomeIcon icon={faChartLine} className="text-purple-600 mr-3" />
                      <div className="space-y-2">
                        <p className="text-sm text-gray-600">This line chart shows how the price changes with each discount.</p>
                      </div>
                    </div>
                    <div className="relative h-[300px]">
                      <Line
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'top',
                            },
                            title: {
                              display: true,
                              text: 'Price Reduction Sequence'
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: {
                                display: true,
                                text: 'Price (₹)'
                              }
                            },
                            x: {
                              title: {
                                display: true,
                                text: 'Discount Stages'
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <h2 className="text-xl font-semibold mb-4 text-gray-800">Discount Breakdown</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-white p-6 rounded-2xl shadow-sm">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Original Price</h3>
                      <div className="space-y-2">
                        <p className="text-gray-600">Starting price: ₹{originalPrice}</p>
                        <p className="text-gray-500">This is the initial price before any discounts.</p>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-2xl shadow-sm">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Sequential Discounts</h3>
                      <div className="space-y-2">
                        <p className="text-gray-600">1st Discount: {discount1}%</p>
                        <p className="text-gray-600">2nd Discount: {discount2}%</p>
                        <p className="text-gray-600">3rd Discount: {discount3}%</p>
                        <p className="text-gray-500">Each discount is applied sequentially to the previous price.</p>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-2xl shadow-sm">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Total Savings</h3>
                      <div className="space-y-2">
                        <p className="text-gray-600">You saved: ₹{totalSavings}</p>
                        <p className="text-gray-500">This is the total amount saved through all three discounts.</p>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-2xl shadow-sm">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Key Concepts</h3>
                      <div className="space-y-2">
                        <p className="text-gray-600">• Sequential discounts compound</p>
                        <p className="text-gray-600">• Each discount reduces the new price</p>
                        <p className="text-gray-600">• Total savings can be significant</p>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-2xl shadow-sm">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Quick Tips</h3>
                      <div className="space-y-2">
                        <p className="text-gray-600">• Higher discounts come first</p>
                        <p className="text-gray-600">• Savings increase with more discounts</p>
                        <p className="text-gray-600">• Always check the final price</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-12">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Features & Benefits</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="text-yellow-500 mr-4" />
                <div>
                  <h3 className="font-semibold text-gray-700">Instant Calculations</h3>
                  <p className="text-gray-600">Get real-time triple discount calculations as you type</p>
                </div>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faChartBar} className="text-purple-500 mr-4" />
                <div>
                  <h3 className="font-semibold text-gray-700">Sequential Discounts</h3>
                  <p className="text-gray-600">Calculate discounts applied one after another</p>
                </div>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faInfoCircle} className="text-green-500 mr-4" />
                <div>
                  <h3 className="font-semibold text-gray-700">Savings Tracker</h3>
                  <p className="text-gray-600">Track your total savings with multiple discounts</p>
                </div>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faStar} className="text-pink-500 mr-4" />
                <div>
                  <h3 className="font-semibold text-gray-700">User-friendly Interface</h3>
                  <p className="text-gray-600">Clean and intuitive design for easy calculations</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">How to Use</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700">Step 1: Enter Prices and Discounts</h3>
                <ol className="list-decimal pl-6 mt-2 space-y-2">
                  <li>Input the original price of the item</li>
                  <li>Enter the first discount percentage</li>
                  <li>Enter the second discount percentage</li>
                  <li>Enter the third discount percentage</li>
                  <li>Use the ₹ prefix for price</li>
                  <li>Use % suffix for discount percentages</li>
                </ol>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700">Step 2: Calculate Triple Discount</h3>
                <ol className="list-decimal pl-6 mt-2 space-y-2">
                  <li>Click the "Calculate Triple Discount" button</li>
                  <li>View the final price after all discounts</li>
                  <li>Check your total savings</li>
                  <li>Review the discount sequence</li>
                  <li>Analyze the price sequence chart</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">FAQ</h2>
            <div className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-xl">
                <h3 className="font-semibold text-gray-700 mb-2">How does sequential discounting work?</h3>
                <p className="text-gray-600">Each discount is applied to the previous price, not the original price. This means the discounts compound on each other.</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl">
                <h3 className="font-semibold text-gray-700 mb-2">Why use three discounts?</h3>
                <p className="text-gray-600">Multiple discounts can lead to significant savings. Retailers often use this strategy to make discounts seem more attractive.</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl">
                <h3 className="font-semibold text-gray-700 mb-2">What's the maximum discount I can get?</h3>
                <p className="text-gray-600">Theoretically, you can get up to 99.99% discount through multiple sequential discounts, but in practice, it's usually limited by the store's policies.</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl">
                <h3 className="font-semibold text-gray-700 mb-2">Can I use this for different items?</h3>
                <p className="text-gray-600">Yes! You can use this calculator for any item. Just enter the original price and discount percentages for each item separately.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripleDiscountCalculator;
